// @ts-check
/**
 * One automated-message trigger: on/off switch, the template it sends, a value for every {{n}}
 * slot, and a phone preview with sample values. Nothing here saves per keystroke - the admin's own
 * Save button commits the template/mapping, and the switch is a separate action with its own rules
 * (see TriggersTab.jsx and useTriggers.js for the write paths this calls into).
 */
import { useEffect, useRef, useState } from 'react';
import { placeholderLabel, templateVariableCount, triggerSampleParams } from '../../utils/whatsapp';
import Modal from '../ui/Modal';
import TemplatePreview from './TemplatePreview';

const fieldClass = 'w-full px-3 py-2 text-sm bg-card border border-input rounded-lg';

const slotsFor = (count) => Array.from({ length: count }, (_v, i) => String(i + 1));

/** Whether every slot in `slots` has the same source in both maps (missing == ''). */
const mapsEqualForSlots = (a, b, slots) => slots.every((s) => (a?.[s] || '') === (b?.[s] || ''));

/** Whole-map equality over every key present in either map - used to notice a real server-side
 * change (the resync effect below), where `mapsEqualForSlots` would miss a change outside the
 * current template's slot count. */
const sameFullMap = (a, b) => mapsEqualForSlots(a, b, [...new Set([...Object.keys(a || {}), ...Object.keys(b || {})])]);

/**
 * @param {{
 *   trigger: import('../../services/adminWhatsAppService').Trigger,
 *   templates: import('../../services/adminWhatsAppService').WhatsAppTemplate[],
 *   onSave: (key: string, patch: {template_id: string | null, variable_map: Record<string, string>}) => Promise<void>,
 *   onSetEnabled: (trigger: import('../../services/adminWhatsAppService').Trigger, enabled: boolean) => Promise<void>,
 * }} props
 */
const TriggerCard = ({ trigger, templates, onSave, onSetEnabled }) => {
  const [templateId, setTemplateId] = useState(trigger.template_id ?? '');
  const [variableMap, setVariableMap] = useState({ ...(trigger.variable_map || {}) });
  const [saving, setSaving] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pending, setPending] = useState(false);
  const pendingRef = useRef(false);
  const prevTriggerRef = useRef(trigger);

  const selectedTemplate = templates.find((t) => t.id === templateId) ?? null;
  const variableCount = selectedTemplate ? templateVariableCount(selectedTemplate.body) : 0;
  const slots = slotsFor(variableCount);

  const savedMap = trigger.variable_map || {};
  const templateDirty = templateId !== (trigger.template_id ?? '');
  const mappingDirty = !mapsEqualForSlots(variableMap, savedMap, slots);
  const dirty = templateDirty || mappingDirty;

  // A template with zero {{n}} placeholders has nothing to map, so an empty slot list already
  // satisfies "every slot mapped" (the backend's own rule) - only require a template to be picked.
  const mappingComplete =
    !!selectedTemplate && slots.every((s) => variableMap[s] && trigger.placeholders.includes(variableMap[s]));
  const canEnable = !dirty && !!templateId && mappingComplete;

  // Re-seed local fields from a server-side change (another admin's edit, or a refresh) only when
  // this card has no unsaved local edits relative to what it last showed - an in-progress edit is
  // never silently clobbered by a reload.
  useEffect(() => {
    const prev = prevTriggerRef.current;
    prevTriggerRef.current = trigger;
    const serverChanged =
      (trigger.template_id ?? '') !== (prev.template_id ?? '') || !sameFullMap(trigger.variable_map, prev.variable_map);
    if (!serverChanged) return;
    const wasInSyncWithPrev = templateId === (prev.template_id ?? '') && sameFullMap(variableMap, prev.variable_map);
    if (wasInSyncWithPrev) {
      setTemplateId(trigger.template_id ?? '');
      setVariableMap({ ...(trigger.variable_map || {}) });
    }
    // Reads templateId/variableMap as of the moment the server value changed, not on every local edit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trigger]);

  let helperText;
  if (trigger.enabled) {
    helperText = 'This message sends automatically.';
  } else if (!templateId) {
    helperText = 'Choose a template to enable this.';
  } else if (!mappingComplete) {
    helperText = 'Map every variable to enable this.';
  } else if (dirty) {
    helperText = 'Save your changes to enable this.';
  } else {
    helperText = 'Off. Turn on to start sending automatically.';
  }
  const switchDisabled = !trigger.enabled && !canEnable;

  const templateOptions = [...templates.map((t) => ({ value: t.id, label: t.name }))];
  if (trigger.template_id && !templateOptions.some((o) => o.value === trigger.template_id)) {
    templateOptions.push({
      value: trigger.template_id,
      label: `${trigger.template_name || 'Unavailable template'} (not approved)`,
    });
  }

  const handleTemplateChange = (value) => {
    setTemplateId(value);
    setVariableMap({});
  };

  const handleMapChange = (slot, value) => {
    setVariableMap((prev) => ({ ...prev, [slot]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const patchMap = Object.fromEntries(slots.filter((s) => variableMap[s]).map((s) => [s, variableMap[s]]));
      await onSave(trigger.key, { template_id: templateId || null, variable_map: patchMap });
    } finally {
      setSaving(false);
    }
  };

  /**
   * Covers a whole enable/disable call end to end. `pendingRef` is a synchronous guard (a second
   * click landing before React re-renders the disabled switch must still be a no-op, the same
   * ref-guard pattern TemplatesTab's sync button uses); `pending` state drives the visible disabled
   * attribute on the switch and Save button.
   */
  const runToggle = async (nextEnabled) => {
    if (pendingRef.current) return;
    pendingRef.current = true;
    setPending(true);
    try {
      await onSetEnabled(trigger, nextEnabled);
    } finally {
      pendingRef.current = false;
      setPending(false);
    }
  };

  const handleSwitchClick = () => {
    if (pendingRef.current) return;
    if (trigger.enabled) {
      runToggle(false);
    } else {
      setConfirmOpen(true);
    }
  };

  const confirmEnable = () => {
    setConfirmOpen(false);
    runToggle(true);
  };

  const previewParams = triggerSampleParams(variableMap, slots);

  return (
    <div data-testid={`trigger-${trigger.key}`} className="p-4 space-y-4 bg-card border border-border rounded-lg">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="text-base font-medium text-foreground">{trigger.label}</h3>
          <p className="mt-1 text-sm text-muted-foreground">{trigger.description}</p>
        </div>
        <div className="flex flex-col items-end shrink-0 gap-1">
          <button
            type="button"
            role="switch"
            aria-checked={trigger.enabled}
            aria-label={`Turn ${trigger.label} on or off`}
            disabled={switchDisabled || pending}
            title={switchDisabled ? helperText : undefined}
            onClick={handleSwitchClick}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
              trigger.enabled ? 'bg-primary' : 'bg-muted-foreground/30'
            }`}
          >
            <span
              aria-hidden="true"
              className={`inline-block h-4 w-4 transform rounded-full bg-card transition-transform ${
                trigger.enabled ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
          </button>
          <p className="text-xs text-right text-muted-foreground max-w-[180px]">{helperText}</p>
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-3">
          <div>
            <label className="block mb-1 text-sm font-medium text-foreground/80" htmlFor={`${trigger.key}-template`}>
              Template
            </label>
            <select
              id={`${trigger.key}-template`}
              aria-label={`${trigger.label} template`}
              value={templateId}
              onChange={(e) => handleTemplateChange(e.target.value)}
              className={fieldClass}
            >
              <option value="">Choose a template...</option>
              {templateOptions.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>

          {slots.map((slot) => (
            <div key={slot}>
              <label className="block mb-1 text-sm text-foreground/80" htmlFor={`${trigger.key}-slot-${slot}`}>
                {`Variable ${slot}`}
              </label>
              <select
                id={`${trigger.key}-slot-${slot}`}
                aria-label={`${trigger.label} variable ${slot}`}
                value={variableMap[slot] || ''}
                onChange={(e) => handleMapChange(slot, e.target.value)}
                className={fieldClass}
              >
                <option value="">Choose...</option>
                {trigger.placeholders.map((p) => (
                  <option key={p} value={p}>
                    {placeholderLabel(p)}
                  </option>
                ))}
              </select>
            </div>
          ))}

          <button
            type="button"
            onClick={handleSave}
            disabled={!dirty || saving || pending}
            className="px-4 py-2 text-sm text-primary-foreground bg-primary rounded-lg hover:bg-primary/90 disabled:opacity-50"
          >
            {saving ? 'Saving...' : 'Save'}
          </button>
        </div>

        <div className="min-w-0">
          {selectedTemplate ? (
            <TemplatePreview template={selectedTemplate} params={previewParams} />
          ) : (
            <div className="flex items-center justify-center h-full p-6 text-sm text-center text-muted-foreground border border-border border-dashed rounded-lg">
              Choose a template to preview this message.
            </div>
          )}
        </div>
      </div>

      {confirmOpen && (
        <Modal onClose={() => setConfirmOpen(false)} maxWidth="sm" fullHeight={false} className="max-h-[80vh]">
          <div className="p-6 space-y-4">
            <h2 className="text-lg font-semibold text-foreground">{`Turn on ${trigger.label}?`}</h2>
            <p className="text-sm text-muted-foreground">Customers will start receiving this message automatically.</p>
            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={() => setConfirmOpen(false)}
                className="px-4 py-2 text-sm border border-input rounded-lg hover:bg-muted/50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmEnable}
                className="px-4 py-2 text-sm text-primary-foreground bg-primary rounded-lg hover:bg-primary/90"
              >
                Turn on
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default TriggerCard;
